import { supabase } from '@/lib/supabase'
import type { Household, HouseholdInvite, HouseholdMember, ProfileLookup } from '@/types/household'
import type { HouseholdRole } from '@/types/enums'
import { requireSessionUser } from './api-utils'

const getHouseholds = async (): Promise<Household[]> => {
  const { data, error } = await supabase
    .from('households')
    .select('*, members:household_members(count)')
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return (data ?? []).map((household: Household & { members?: Array<{ count?: number }> }) => ({
    ...household,
    member_count: household.members?.[0]?.count ?? household.member_count ?? 0,
  }))
}

const createHousehold = async (name: string): Promise<Household> => {
  const user = await requireSessionUser()
  const { data, error } = await supabase
    .from('households')
    .insert({ name, created_by: user.id })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const updateHousehold = async (id: string, payload: { name: string }): Promise<Household> => {
  const { data, error } = await supabase
    .from('households')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const deleteHousehold = async (id: string): Promise<void> => {
  const { error } = await supabase.from('households').delete().eq('id', id)
  if (error) {
    throw error
  }
}

const getHouseholdMembers = async (householdId: string): Promise<HouseholdMember[]> => {
  const { data, error } = await supabase
    .from('household_members')
    .select('*')
    .eq('household_id', householdId)
    .order('joined_at', { ascending: true })

  if (error) {
    throw error
  }

  return data ?? []
}

const removeHouseholdMember = async (householdId: string, memberId: string): Promise<void> => {
  const { error } = await supabase
    .from('household_members')
    .delete()
    .eq('household_id', householdId)
    .eq('id', memberId)

  if (error) {
    throw error
  }
}

const updateHouseholdMemberRole = async (
  householdId: string,
  memberId: string,
  role: HouseholdRole
): Promise<HouseholdMember> => {
  const { data, error } = await supabase
    .from('household_members')
    .update({ role })
    .eq('household_id', householdId)
    .eq('id', memberId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const leaveHousehold = async (householdId: string): Promise<void> => {
  const user = await requireSessionUser()
  const { error } = await supabase
    .from('household_members')
    .delete()
    .eq('household_id', householdId)
    .eq('user_id', user.id)

  if (error) {
    throw error
  }
}

const getHouseholdInvites = async (householdId: string): Promise<HouseholdInvite[]> => {
  const { data, error } = await supabase
    .from('household_invites')
    .select('*')
    .eq('household_id', householdId)
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return data ?? []
}

const createHouseholdInvite = async (
  householdId: string,
  email: string,
  role: HouseholdRole
): Promise<HouseholdInvite> => {
  const user = await requireSessionUser()
  const inviteCode = crypto.randomUUID()
  const expiresAt = new Date()
  expiresAt.setDate(expiresAt.getDate() + 7)

  const { data, error } = await supabase
    .from('household_invites')
    .insert({
      household_id: householdId,
      email,
      role,
      invite_code: inviteCode,
      expires_at: expiresAt.toISOString(),
      created_by: user.id,
    })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const findUserByEmail = async (email: string): Promise<ProfileLookup | null> => {
  const { data, error } = await supabase.rpc('find_profile_by_email', {
    candidate_email: email.trim().toLowerCase(),
  })

  if (error) {
    if (error.message?.includes('find_profile_by_email')) {
      throw new Error('Gebruikerscontrole is nog niet geconfigureerd. Run eerst de nieuwe Supabase repair SQL.')
    }

    throw error
  }

  if (Array.isArray(data)) {
    return data[0] ?? null
  }

  return (data as ProfileLookup | null) ?? null
}

const deleteHouseholdInvite = async (inviteId: string): Promise<void> => {
  const { error } = await supabase.from('household_invites').delete().eq('id', inviteId)
  if (error) {
    throw error
  }
}

const getHousehold = async (id: string): Promise<Household> => {
  const { data, error } = await supabase.from('households').select('*').eq('id', id).single()
  if (error) {
    throw error
  }
  return data
}

const acceptHouseholdInvite = async (inviteCode: string): Promise<Household> => {
  const user = await requireSessionUser()
  const { data: invite, error: inviteError } = await supabase
    .from('household_invites')
    .select('*')
    .eq('invite_code', inviteCode)
    .gt('expires_at', new Date().toISOString())
    .single()

  if (inviteError) {
    throw inviteError
  }

  const { error: memberError } = await supabase
    .from('household_members')
    .insert({
      household_id: invite.household_id,
      user_id: user.id,
      role: invite.role,
    })

  if (memberError) {
    throw memberError
  }

  await deleteHouseholdInvite(invite.id)
  return getHousehold(invite.household_id)
}

export const householdApi = {
  getHouseholds,
  createHousehold,
  updateHousehold,
  deleteHousehold,
  getHouseholdMembers,
  removeHouseholdMember,
  updateHouseholdMemberRole,
  leaveHousehold,
  getHouseholdInvites,
  createHouseholdInvite,
  findUserByEmail,
  deleteHouseholdInvite,
  acceptHouseholdInvite,
  getHousehold,
}
